import { type CSSProperties, useEffect, useRef, useState } from 'react';

const SCRAMBLE_CHARS = 'abcdefghijklmnopqrst';

function randomScrambleChar(): string {
    return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)] ?? '';
}

function scrambleCharAt(target: string, index: number, charProgress: number): string | null {
    if (index >= target.length) {
        return charProgress >= 1 ? null : randomScrambleChar();
    }
    if (charProgress >= 1) return target[index] ?? '';
    if (target[index] === ' ') return ' ';
    return randomScrambleChar();
}

function buildScrambleFrame(target: string, maxLen: number, progress: number): string {
    let result = '';
    for (let i = 0; i < maxLen; i++) {
        const charProgress = Math.min(1, (progress * maxLen - i * 0.6) / (maxLen * 0.4));
        const next = scrambleCharAt(target, i, charProgress);
        if (next !== null) result += next;
    }
    return result;
}

export type ScrambleTextProps = {
    readonly text: string;
    readonly className?: string;
    readonly style?: CSSProperties;
    readonly shimmer?: boolean;
};

export function ScrambleText({ text, className, style, shimmer = true }: ScrambleTextProps) {
    const [display, setDisplay] = useState(text);
    const displayRef = useRef(text);
    const prevTextRef = useRef(text);
    const frameRef = useRef<number>(0);

    displayRef.current = display;

    useEffect(() => {
        if (text === prevTextRef.current) {
            setDisplay(text);
            return;
        }
        prevTextRef.current = text;

        const target = text;
        const maxLen = Math.max(displayRef.current.length, target.length);
        const totalFrames = 20;
        const frameDuration = 40;
        let frame = 0;
        let lastTime = 0;

        const step = (time: number) => {
            if (lastTime && time - lastTime < frameDuration) {
                frameRef.current = requestAnimationFrame(step);
                return;
            }
            lastTime = time;
            frame++;
            setDisplay(buildScrambleFrame(target, maxLen, frame / totalFrames));
            if (frame < totalFrames) {
                frameRef.current = requestAnimationFrame(step);
            }
        };
        frameRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frameRef.current);
    }, [text]);

    const shimmerStyle: CSSProperties = shimmer
        ? {
              backgroundImage:
                  'linear-gradient(90deg, currentColor 0%, currentColor 40%, rgba(156,163,175,0.4) 50%, currentColor 60%, currentColor 100%)',
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'shimmer-text 2.5s ease-in-out infinite',
          }
        : {};

    return (
        <span className={className} style={{ ...style, ...shimmerStyle }}>
            {display}
        </span>
    );
}

export type ThinkingCycleProps = {
    readonly texts: readonly [string, ...string[]];
    readonly minDelayMs?: number;
    readonly maxDelayMs?: number;
    readonly className?: string;
};

export function ThinkingCycle({ texts, minDelayMs = 2000, maxDelayMs = 5000, className }: ThinkingCycleProps) {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const scheduleNext = () => {
            const delay = minDelayMs + Math.random() * (maxDelayMs - minDelayMs);
            timer = setTimeout(() => {
                setIndex((prev) => (prev + 1) % texts.length);
                scheduleNext();
            }, delay);
        };
        scheduleNext();
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [texts.length, minDelayMs, maxDelayMs]);

    return (
        <div className="flex items-center gap-m">
            <span
                aria-hidden={true}
                className="inline-block h-[16px] w-[3px] rounded-full bg-black-700"
                style={{ animation: 'blink-cursor 1s step-end infinite' }}
            />
            <ScrambleText
                text={texts[index] ?? texts[0]}
                className={className ?? 'font-sans text-sm font-normal tracking-tight text-gray-500'}
            />
        </div>
    );
}
